go-card {
  /**
    @prop --go-card-outer-padding:
      Outer padding of card.
  */
  --go-card-outer-padding: 1.25rem 1rem;

  /**
    @prop --go-card-inner-gap:
      Gap size in between inner sections of card. ie. heading, content, footer
  */
  --go-card-inner-gap: 1rem;

  /**
    @prop --go-card-bg:
      Background colour of card
  */
  --go-card-bg: var(--go-color-bg);

  /**
    @prop --go-card-fg:
      Foreground (text) color for the card
  */
  --go-card-fg: var(--go-color-text);

  /**
    @prop --go-card-heading-font-size:
      Font size of card heading text
  */
  --go-card-heading-font-size: var(--go-size-2);

  /**
    @prop --go-card-heading-font-weight:
      Font weight of card heading text
  */
  --go-card-heading-font-weight: 500;

  /**
    @prop --go-card-heading-color:
      Text colour of card heading
  */
  --go-card-heading-color: var(--go-card-fg);

  /**
    @prop --go-card-subheading-font-size:
      Font size of subheading
  */
  --go-card-subheading-font-size: var(--go-size-1);

  /**
    @prop --go-card-subheading-font-weight:
      Font weight of subheading
  */
  --go-card-subheading-font-weight: 400;

  /**
    @prop --go-card-subheading-text-color:
      Text colour of subheading
  */
  --go-card-subheading-color: var(--go-color-neutral-600);

  /**
    @prop --go-card-border-width:
      Border width for `border` card
      - default: 1px
  */
  --go-card-border-width: 1px;

  /**
    @prop --go-card-border-color:
      Border color for card (for `border` card only)
      - default: var(--go-color-neutral-300)
  */
  --go-card-border-color: var(--go-color-neutral-300);

  /**
    @prop --go-card-media-basis:
      Flex basis for media position = left|right
  */
  --go-card-media-basis: 40%;

  /**
    @prop --go-card-media-height:
      Height of media for media position = top|bottom
  */
  --go-card-media-height: auto;

  /**
    @prop --go-card-media-aspect-ratio:
      Aspect ratio for media for when media position = top|bottom
  */
  --go-card-media-aspect-ratio: auto;

  /**
    @prop --go-card-media-object-position: [Object-position rule](https://developer.mozilla.org/en-US/docs/Web/CSS/object-position) for the media. default: center
  */
  --go-card-media-object-position: center;

  /**
    @prop --go-card-radius:
      Border radius for card
  */
  --go-card-radius: var(--radius-2);

  /**
    @prop --go-card-shadow:
      Card shadow
  */
  --go-card-shadow: var(--shadow-2);

  display: block;
  background: var(--go-card-bg);
  color: var(--go-card-text);
  border-radius: var(--go-card-radius);
  box-shadow: var(--go-card-shadow);
  width: 100%;
  height: 100%;

  .card-inner {
    padding: var(--go-card-outer-padding);
    overflow: auto;
    display: flex;
    height: 100%;
    flex: 1;
    flex-direction: column;
    gap: var(--go-card-inner-gap);
    .card-heading-section {
      .card-heading {
        font-size: var(--go-card-heading-font-size);
        font-weight: var(--go-card-heading-font-weight);
        color: var(--go-card-heading-color);
      }
      .card-subheading {
        font-size: var(--go-card-subheading-font-size);
        font-weight: var(--go-card-subheading-font-weight);
        color: var(--go-card-subheading-color);
      }
    }
  }

  .card-media {
    overflow: hidden;
    flex-basis: var(--go-card-media-basis);
    img,
    video,
    object,
    iframe {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: var(--go-card-media-object-position);
      aspect-ratio: var(--go-card-media-aspect-ratio);
    }
  }
  &.media {
    display: flex;
    justify-content: space-between;
    overflow: hidden;
    &.top,
    &.bottom {
      .card-media {
        width: 100%;
        height: var(--go-card-media-height);
        flex-basis: auto;
      }
    }
    &.top {
      flex-direction: column;
      .card-inner {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      }
    }
    &.bottom {
      flex-direction: column-reverse;
      .card-inner {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
    &.left {
      flex-direction: row;
      align-items: stretch;
      .card-media {
        width: var(--go-card-media-width);
      }
    }
    &.right {
      flex-direction: row-reverse;
      align-items: stretch;
      .card-media {
        width: var(--go-card-media-width);
      }
    }
  }
  &.has-link {
    position: relative;
    &:focus-within {
      outline: 2px solid var(--go-color-link);
      outline-offset: 2px;
    }
    .card-heading {
      .go-link {
        &:focus-visible {
          outline: none; // stylelint-disable-line
        }
      }
    }
  }
  &.flat {
    --go-card-shadow: none;
  }
  &.border {
    border: var(--go-card-border-width) solid var(--go-card-border-color);
  }

  // content
  .card-content {
    flex: 1 0 auto;
    line-height: 1.5;
  }

  // footer
  .card-footer {
    flex: 0 0 auto;
  }
}

.equal-height-row {
  align-items: stretch;
}
